import { MAIN_ROUTES, MainRoutesProps } from 'routes';
import './LeftNavigationBar.css';
import { useNavigate, Link } from 'react-router';
import { useSelector } from 'react-redux';
import MagicButton from 'components/MagicButton/MagicButton';
import { useIsAdmin } from 'hooks/useIsAdmin';
import { useIsElite } from 'hooks/useIsElite';
import { useUserRoles } from 'hooks/useUserRoles';
import { useState, forwardRef } from 'react';
import FeedbackModal from 'components/FeedbackModal/FeedbackModal';
import { useSubmitFeedbackMutation } from 'store/services/feedback/feedbackApi';
import { toast } from 'react-toastify';
import { AssetUrls } from 'config/assets';
import { useGetUnreadCountQuery } from 'store/services/message/messageApi';
import { useGetInboundTradeCountQuery } from 'store/services/trade/tradeApi';
import { useGetOnboardingChecklistQuery } from 'store/services/onboarding/onboardingApi';
import { RootState } from 'store';
import { ConnectionStatus } from 'components/ConnectionStatus';
import { useSidebarCollapse } from 'context/SidebarCollapseContext';
import ChevronLeftIcon from 'components/Icons/ChevronLeftIcon';
import EliteUpsellBanner from 'components/EliteUpsellBanner/EliteUpsellBanner';

interface Props {
  currentTab: MainRoutesProps;
  isMobile?: boolean;
  isMobileMenuOpen?: boolean;
  onMenuClose?: () => void;
  isPublicMode?: boolean;
  onNavigationAttempt?: () => void;
  isAuthenticated?: boolean;
}

// Routes that are publicly accessible without authentication
const PUBLIC_ROUTES = ['/articles', '/search', '/faq', '/compare', '/sets', '/leaderboard', '/elite'];

// Critical tasks that should show red badge when incomplete
const CRITICAL_TASK_KEYS = [
  'VERIFY_PHONE',
  'VERIFY_EMAIL',
  'ADD_ADDRESS',
  'ADD_WANT_1',
  'ADD_WANT_10',
  'SEND_FIRST_TRADE',
  'ADD_WANT_50',
];

const getNavigationBarItems = (
  currentTab: MainRoutesProps,
  isMobile: boolean,
  onMenuClose?: () => void,
  isPublicMode?: boolean,
  onNavigationAttempt?: () => void,
  isAuthenticated?: boolean,
  unreadCount?: number,
  inboundTradeCount?: number,
  hasOverdueTrades?: boolean,
  onboardingIncompleteCount?: number,
  hasCriticalIncomplete?: boolean
) => {
  return MAIN_ROUTES.filter(
    (route) => route.path !== '/search' && route.path !== '/newsfeed',
  ).map(
    (route) => {
      const isSelected = currentTab.path === route.path;
      const icon = isSelected ? route.icons?.selected : route.icons?.default;
      const labelClassName = `left-navigation-bar-item-label ${
        route.path === currentTab.path ? 'selected' : ''
      }`;
      const itemClassName = `left-navigation-bar-item ${
        route.path === currentTab.path ? 'selected' : ''
      }`;

      const isInbox = route.path === '/inbox';
      const showInboxBadge = isInbox && unreadCount !== undefined && unreadCount > 0;

      const isReceiving = route.path === '/receiving';
      const showTradeBadge = isReceiving && inboundTradeCount !== undefined && inboundTradeCount > 0;
      const tradeBadgeClassName = `inbound-trade-badge ${hasOverdueTrades ? 'overdue' : 'normal'}`;

      // Home badge logic
      const isHome = route.path === '/home';
      const showHomeBadge = isHome && onboardingIncompleteCount !== undefined && onboardingIncompleteCount > 0;
      const homeBadgeClassName = `home-badge ${hasCriticalIncomplete ? 'urgent' : 'normal'}`;

      return (
        <Link
          key={`left-navigation-bar-item-${route.title}`}
          to={route.path}
          className={itemClassName}
          data-tooltip={route.title}
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            // In public mode, allow navigation to public routes, block protected routes
            if (isPublicMode && !isAuthenticated && onNavigationAttempt) {
              // Allow navigation to public routes
              if (!PUBLIC_ROUTES.includes(route.path)) {
                e.preventDefault();
                onNavigationAttempt();
                return;
              }
            }
            if (isMobile && onMenuClose) {
              onMenuClose();
            }
          }}
        >
          <img src={icon} alt="navigation-icon" />
          <div className={labelClassName}>{route.title}</div>
          {showInboxBadge && unreadCount !== undefined && (
            <span className="unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
          {showTradeBadge && inboundTradeCount !== undefined && (
            <span className={tradeBadgeClassName}>{inboundTradeCount > 99 ? '99+' : inboundTradeCount}</span>
          )}
          {showHomeBadge && onboardingIncompleteCount !== undefined && (
            <span className={homeBadgeClassName}>{onboardingIncompleteCount}</span>
          )}
        </Link>
      );
    }
  );
};

export const LeftNavigationBar = forwardRef<HTMLDivElement, Props>(({ currentTab, isMobile = false, isMobileMenuOpen = false, onMenuClose, isPublicMode = false, onNavigationAttempt, isAuthenticated = false }, ref) => {
  const navigate = useNavigate();
  const { isCollapsed, toggleCollapse } = useSidebarCollapse();
  const effectiveCollapsed = !isMobile && isCollapsed;
  const { isAdmin } = useIsAdmin(isPublicMode && !isAuthenticated); // Only skip if unauthenticated in public mode
  const { isElite, isEliteStatusResolved } = useIsElite(isPublicMode && !isAuthenticated);
  const { hasAdminAccess } = useUserRoles(isPublicMode && !isAuthenticated);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [submitFeedback, { isLoading: isSubmittingFeedback }] = useSubmitFeedbackMutation();

  // Redux state for rate limiting, maintenance mode, and socket connection
  const isRateLimited = useSelector((state: RootState) => state.rateLimit.isRateLimited);
  const isMaintenanceMode = useSelector((state: RootState) => state.maintenance.isMaintenanceModeActive);
  const isSocketConnected = useSelector((state: RootState) => state.message.isConnected);

  // Socket-based polling: Use WebSocket when connected, fallback to 60s polling when disconnected
  // When socket is connected, badges update in real-time via notifications:update events
  const pollingInterval = (isRateLimited || isMaintenanceMode)
    ? 0
    : isSocketConnected ? 0 : 60000; // No polling when connected, 60s fallback when disconnected

  // Fetch unread count (real-time via WebSocket, fallback polling when disconnected)
  const { data: unreadData } = useGetUnreadCountQuery(undefined, {
    pollingInterval,
    refetchOnMountOrArgChange: true,
    skip: isPublicMode,
  });

  // Fetch inbound trade count (real-time via WebSocket, fallback polling when disconnected)
  const { data: inboundTradeData } = useGetInboundTradeCountQuery(undefined, {
    pollingInterval,
    refetchOnMountOrArgChange: true,
    skip: isPublicMode,
  });

  // Fetch onboarding checklist for home badge (skip in public mode)
  const { data: onboardingData } = useGetOnboardingChecklistQuery(undefined, {
    skip: isPublicMode,
  });

  const handleFeedbackSubmit = async (feedbackData: any) => {
    try {
      const result = await submitFeedback(feedbackData).unwrap();
      if (result.success) {
        toast.success('Thank you for your feedback!');
        setIsFeedbackModalOpen(false);
      } else {
        toast.error(result.message || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    }
  };

  const isSearchSelected = currentTab.path === '/search';
  const searchClassName = `left-navigation-bar-item-label ${
    isSearchSelected ? 'selected' : ''
  }`;
  const searchItemClassName = `left-navigation-bar-item ${
    isSearchSelected ? 'selected' : ''
  }`;

  const unreadCount = unreadData?.count || 0;
  const inboundTradeCount = inboundTradeData?.count || 0;
  const hasOverdueTrades = inboundTradeData?.hasOverdueTrades || false;

  // Calculate onboarding badge values
  const incompleteTasks = (onboardingData?.tasks ?? []).filter(t => t.status !== 'COMPLETED');
  const onboardingIncompleteCount = incompleteTasks.length;
  const hasCriticalIncomplete = incompleteTasks.some(t => CRITICAL_TASK_KEYS.includes(t.taskKey));

  const navigationBarItems = getNavigationBarItems(
    currentTab,
    isMobile,
    onMenuClose,
    isPublicMode,
    onNavigationAttempt,
    isAuthenticated,
    unreadCount,
    inboundTradeCount,
    hasOverdueTrades,
    onboardingIncompleteCount,
    hasCriticalIncomplete
  );

  return (
    <div ref={ref} className={`left-navigation-bar ${isMobile ? 'mobile' : ''} ${isMobileMenuOpen ? 'mobile-open' : ''} ${effectiveCollapsed ? 'collapsed' : ''}`}>
      <div className="left-navigation-bar-sticky">
      {/* Logo Header */}
      <div className="left-navigation-bar-header">
        <img
          src={AssetUrls.logos.iconLogo}
          width={30}
          alt="icon-logo"
        />
        <div className="left-navigation-bar-header-title">trademagic.gg</div>
        {!isMobile && (
          <button
            className={`sidebar-collapse-toggle ${effectiveCollapsed ? 'collapsed' : ''}`}
            onClick={toggleCollapse}
            aria-label={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronLeftIcon size={14} color="#878787" />
          </button>
        )}
      </div>

      {/* Send Cards Button - Wrapped in div to handle stopPropagation */}
      <div className="sidebar-send-cards-wrapper" data-tooltip="Send Cards" onClick={(event) => event.stopPropagation()}>
        <MagicButton
          label="Send Cards"
          icon={
            <img
              src={AssetUrls.logos.arevakhachIcon}
              alt="send-icon"
              style={{ width: 20, height: 20 }}
            />
          }
          color="purple"
          fullWidth
          onPress={() => {
            // In public mode, only prevent navigation if user is not authenticated
            if (isPublicMode && !isAuthenticated && onNavigationAttempt) {
              onNavigationAttempt();
            } else {
              navigate('/sendcards');
              if (isMobile && onMenuClose) {
                onMenuClose();
              }
            }
          }}
        />
      </div>

      {/* Navigation Items */}
      <div className="left-navigation-bar-items-container">
        {/* Search Item - Always allowed (public route) */}
        <Link
          to="/search"
          className={searchItemClassName}
          data-tooltip="Search"
          onClick={() => {
            // Search is a public route, always allow navigation
            if (isMobile && onMenuClose) {
              onMenuClose();
            }
          }}
        >
          <img
            src={AssetUrls.icons.navigation.search}
            alt="search-icon"
          />
          <div className={searchClassName}>Search</div>
        </Link>

        {/* Existing routes from MAIN_ROUTES */}
        {navigationBarItems}

        {/* Feedback Link */}
        <div
          className="left-navigation-bar-item"
          data-tooltip="Feedback"
          onClick={(event) => {
            event.stopPropagation();
            // In public mode, only prevent navigation if user is not authenticated
            if (isPublicMode && !isAuthenticated && onNavigationAttempt) {
              onNavigationAttempt();
            } else {
              setIsFeedbackModalOpen(true);
            }
          }}
        >
          <img
            src={AssetUrls.icons.navigation.adminFeedback}
            alt="feedback-icon"
          />
          <div className="left-navigation-bar-item-label">Feedback</div>
        </div>

        {/* Elite Link / Upsell Banner — wait until status resolves to avoid flash */}
        {!isEliteStatusResolved ? null : isElite ? (
          <Link
            to="/elite"
            className={`left-navigation-bar-item${currentTab.path === '/elite' ? ' selected' : ''}`}
            data-tooltip="Elite"
            onClick={() => {
              if (isMobile && onMenuClose) {
                onMenuClose();
              }
            }}
          >
            <img
              src={AssetUrls.icons.elite.badge}
              alt="elite-icon"
            />
            <div className={`left-navigation-bar-item-label${currentTab.path === '/elite' ? ' selected' : ''}`}>
              Elite
            </div>
          </Link>
        ) : (
          !effectiveCollapsed && (
            <EliteUpsellBanner
              variant="sidebar"
              refSource="sidebar"
              onNavigate={() => {
                if (isMobile && onMenuClose) {
                  onMenuClose();
                }
              }}
            />
          )
        )}

        {/* Admin Link - Visible to admins and writers */}
        {hasAdminAccess && (
          <Link
            to={isAdmin ? "/admin/feedback" : "/admin/blog/articles"}
            className="left-navigation-bar-item"
            data-tooltip="Admin"
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              // In public mode, only prevent navigation if user is not authenticated
              if (isPublicMode && !isAuthenticated && onNavigationAttempt) {
                e.preventDefault();
                onNavigationAttempt();
                return;
              }
              if (isMobile && onMenuClose) {
                onMenuClose();
              }
            }}
          >
            <img
              src={AssetUrls.icons.navigation.admin}
              alt="admin-icon"
            />
            <div className="left-navigation-bar-item-label">Admin</div>
          </Link>
        )}
      </div>

      {/* Connection Status Indicator - Shows when WebSocket is disconnected */}
      {!isPublicMode && <ConnectionStatus className="left-nav-connection-status" />}
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        onSubmit={handleFeedbackSubmit}
        isLoading={isSubmittingFeedback}
      />
    </div>
  );
});

LeftNavigationBar.displayName = 'LeftNavigationBar';
